import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { getAdminConstructionApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import {
  BaseCombinedStore,
  createPaginatedStore,
} from './helpers/createDataModificationStore';
import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import { User } from './types';

type DeletedUsersContextValue = {
  data: User[];
  loading: boolean;
  error: null | unknown;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

const PAGE_LIMIT = 200;

const useDeletedUsersStore = () =>
  createPaginatedStore<User>({
    contextName: CONTEXT_NAMES.DeletedUsersContext,
  })();

/**
 * Context for deleted users list.
 */
const DeletedUsersContext = getOrCreateGlobalContext<DeletedUsersContextValue>(
  CONTEXT_NAMES.DeletedUsersContext,
);

const useDeletedUsersApiUrl = () => {
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminConstructionApiUrl();
  return projectId
    ? `${adminApiUrl}/v1/projects/${projectId}/users?filter[status]=deleted&limit=${PAGE_LIMIT}`
    : null;
};

type UseDeletedUsersOptionsType = {
  disable?: boolean;
};

const useDeletedUsersStoreWithValidation = () =>
  useStoreWithValidation<BaseCombinedStore<User>>(
    CONTEXT_NAMES.DeletedUsersContext,
    {
      hook: 'useDeletedUsersContext',
      provider: 'DeletedUsersProvider',
    },
  ) || {};

/**
 * Fetches the paginated list of deleted users for the current project. The fetch can be skipped by passing `disable: true`.
 */
export const useDeletedUsers = (options?: UseDeletedUsersOptionsType) => {
  const url = useDeletedUsersApiUrl();
  const { entities, error, loading, fetchPage, retry } =
    useDeletedUsersStoreWithValidation();

  const shouldFetchDeletedUsers = !options?.disable;

  useEffect(() => {
    if (shouldFetchDeletedUsers) {
      fetchPage(url);
    }
  }, [url, shouldFetchDeletedUsers, fetchPage]);

  return {
    data: entities,
    loading: options?.disable ? false : loading,
    error,
    retry,
    setEnabled: noop,
  };
};

const useDeletedUsersProvider = () => {
  const url = useDeletedUsersApiUrl();
  const { entities, error, loading, retry, fetchPage } = useDeletedUsersStore();

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.DeletedUsersContext);
    fetchPage(url);
  }, [url, fetchPage]);

  return useMemo(
    () => ({
      data: entities,
      loading,
      error,
      retry,
      setEnabled: legacySetEnabled,
    }),
    [entities, loading, error, retry, legacySetEnabled],
  );
};

export const DeletedUsersProvider: FC<PropsWithChildren> = ({ children }) => {
  const value = useDeletedUsersProvider();

  return (
    <DeletedUsersContext.Provider value={value}>
      {children}
    </DeletedUsersContext.Provider>
  );
};

DeletedUsersProvider.displayName = 'DeletedUsersProvider';
